import axios from 'axios';
import { LEARNING_PLAN_UNIT_TYPE } from '../components/LearningPlan/LearningPlanUnit';
import { LearningPlan } from '../hydra';

export type CompaniesResponse = {
  items: Array<{
    id: number;
    name: string;
    slug: string;
    latestCohortStartDate: string | null;
    latestCohortEndDate: string | null;
    numLearners: number;
  }>;
};

export async function getCompanies(token: string) {
  const response = await axios.get<CompaniesResponse>('/admin/companies', {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
}

export async function addCompany(
  token: string,
  {
    name,
    slug,
    slackDiscussChannelId,
    slackStatusChannelId,
  }: {
    name: string;
    slug: string;
    slackDiscussChannelId: string;
    slackStatusChannelId: string;
  }
) {
  const response = await axios.post(
    '/admin/companies',
    {
      name,
      slug,
      slackDiscussChannelId,
      slackStatusChannelId,
    },
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return response.data;
}

export enum LEARNER_STATUS {
  INVITE_PENDING = 'Invite Pending',
  INVITE_EXPIRED = 'Invite Expired',
  ONBOARDED = 'Onboarded',
  RESTARTED = 'Restarted',
}

export type LearningPlanModule = {
  content: {
    id: number;
    title: string;
    slug: string;
  };
  topic: string;
  topicIconUrl: string;
  type: LEARNING_PLAN_UNIT_TYPE.CONTENT_MODULE;
  didOnboard: boolean;
  wasInvited: boolean;
  coreLessonsCount: number | null;
  coreSubmittedLessonsCount: number | null;
  stretchLessonsCount: number | null;
  stretchSubmittedLessonsCount: number | null;
  latestCohortStartDate: Date | null;
  latestCohortEndDate: Date | null;
};

export type LearningPlanProject = {
  projectId: number;
  topic: string;
  type: LEARNING_PLAN_UNIT_TYPE.PROJECT;
  topicIconUrl: string;
  title: string;
  exercisesCount: number | null;
  submittedExercisesCount: number | null;
  slug: string;
};
export type CompanyResponse = {
  name: string;
  slug: string;
  createdAt: Date;
  learners: Array<{
    id: number;
    name: string;
    email: string;
    addedAt: Date;
    lastActiveAt: Date | null;
    learningPlan: Array<LearningPlanModule | LearningPlanProject> | null;
  }>;
  cohorts: Array<{
    cycleId: number;
    startDate: Date;
    endDate: Date;
    isOpen: boolean;
    learners: Array<{
      learnerId: number;
      sessionId: number | null;
      inviteId: number | null;
      name: string;
      email: string;
      status: LEARNER_STATUS;
      invitedAt: Date;
      onboardedOrRestartedAt: Date | null;
      lastActiveAt: Date | null;
      onboardingAnswers: {
        availability: string | null;
        timezone: {
          timezone: string;
          utcOffset: number;
          localHour: number;
          utcHour: number;
          isAnswerValid: boolean;
        } | null;
      };
      module: {
        contentId: number;
        trackId: number;
        contentTitle: string;
        contentSlug: string;
        topic: string;
        topicIconUrl: string;
        coreLessonsCount: number | null;
        coreSubmittedLessonsCount: number | null;
        stretchLessonsCount: number | null;
        stretchSubmittedLessonsCount: number | null;
        aiMessageCount: number | null;
      };
      mentor: {
        profileId: number;
        assignmentId: number;
        name: string;
        email: string;
      } | null;
    }>;
  }>;
  modules: Array<{
    contentId: number;
    contentTitle: string;
    contentSlug: string;
    createdAt: Date;
    topicIconUrl: string;
    topic: string;
    hasOnboardingSurvey: boolean;
    hasOnboardingEmail: boolean;
    isDeprecated: boolean;
    isDraft: boolean;
  }>;
  projects: Array<{
    projectId: number;
    topic: string;
    topicIconUrl: string;
    title: string;
    isDraft: boolean;
    slug: string;
  }>;
  slack: {
    discussChannel: {
      id: string;
      name: string;
    } | null;
    statusChannel: {
      id: string;
      name: string;
    } | null;
  };
  mentors: Array<{
    id: number;
    name: string;
    email: string;
    topics: Array<string>;
  }>;
};

export async function getCompany(token: string, id: number) {
  const response = await axios.get<CompanyResponse>(`/admin/companies/${id}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
}

export async function addLearners(
  token: string,
  { learners, id }: { learners: { name: string; email: string }[]; id: string }
) {
  const response = await axios.post(
    `/admin/companies/${id}/learners`,
    {
      learners,
    },
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return response.data;
}

export async function addCohort(
  token: string,
  {
    companyId,
    startDate,
    endDate,
  }: { companyId: string; startDate: string; endDate: string }
) {
  const response = await axios.post(
    `/admin/companies/${companyId}/cohorts`,
    { startDate, endDate },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data;
}

export async function inviteLearnersToCohort(
  token: string,
  {
    companyId,
    cohortId,
    learnerIds,
    contentId,
  }: {
    companyId: number;
    cohortId: number;
    learnerIds: number[];
    contentId: number;
  }
) {
  const response = await axios.post(
    `/admin/companies/${companyId}/cohort/${cohortId}/invite`,
    { learnerIds, contentId },
    { headers: { Authorization: `Bearer ${token}` } }
  );

  return response.data;
}

type SlackChannelsResponse = {
  items: Array<{
    id: string;
    name: string;
  }>;
};

export async function getSlackChannels(token: string) {
  const response = await axios.get<SlackChannelsResponse>(
    `/admin/slack-channels`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data;
}

export async function assignLearnersToCohortMentor(
  token: string,
  {
    companyId,
    cohortId,
    sessionIds,
    mentorProfileId,
    oneOnOneTimestamps,
  }: {
    companyId: number;
    cohortId: number;
    sessionIds: number[];
    mentorProfileId: number;
    oneOnOneTimestamps: Date[];
  }
) {
  const response = await axios.post(
    `/admin/companies/${companyId}/cohort/${cohortId}/assign-mentor`,
    { sessionIds, mentorProfileId, oneOnOneTimestamps },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data;
}

export async function editLearningPlans(
  token: string,
  {
    companyId,
    learners,
  }: {
    companyId: number;
    learners: { id: number; learningPlan: LearningPlan }[];
  }
) {
  const response = await axios.post(
    `/admin/companies/${companyId}/learning-plans`,
    { learners },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data;
}
